/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * Product Offerings Sagas for select MegaPowers step
 *
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.27.0
 */

import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import {
    API_HOSTNAME,
    PRODUCT_OFFERINGS_GET_MEGA_POWERS_LIST,
    PRODUCT_OFFERINGS_UPDATE_MEGA_POWERS,
    WIZARD,
    WIZARD_ACTION,
} from '@/main/modules/app/Api';
import { EHttpMethod } from '@/main/modules/app/appConstants';
import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { makeSelectTranslationWizardProductOfferings } from '@/main/modules/internationalization/intSelectors';
import { mapMegaPowersToActivatedAndDeactivated } from '@/main/modules/wizard/utils/mapMegaPowersToActivatedAndDeactivated';
import {
    wizardLoaderStartAction,
    wizardLoaderStopAction,
    wizardStorageUpdateStepAction,
} from '@/main/modules/wizard/wizardActions';
import { EWizardName } from '@/main/modules/wizard/wizardConstants';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction } from '@/main/modules/wizard/wizardTypes';
import { checkResponseErrorWithModal, requestSaga } from '@/main/sagas/requestSaga';
import { stringFormat } from '@/main/utils/stringFormat';

import { productOfferingsSetParamsAction } from './wizardProductOfferingsActions';
import {
    makeSelectProductOfferingsInitialMegaPowers,
    makeSelectProductOfferingsMegaPowers,
} from './wizardProductOfferingsSelectors';

/*
 * -------------------------------------------
 *       INIT WIZARD WITH CURRENT STEP
 * -------------------------------------------
 */

export function* wizardProductOfferingsStepSelectMegaPowersInitSaga(action: TWizardMetaSuccessAction): SagaIterator {
    yield call(wizardProductOfferingsStepSelectMegaPowersGetList);

    yield call(productOfferingsStepSelectMegaPowersLoadParams, action.payload.step);
}

/*
 * ------------------------------------------
 *       GET MEGA POWERS LIST
 * -------------------------------------------
 */

export function* wizardProductOfferingsStepSelectMegaPowersGetList(): SagaIterator {
    try {
        const url =
            API_HOSTNAME +
            stringFormat(WIZARD, EWizardName.PRODUCT_OFFERINGS) +
            stringFormat(WIZARD_ACTION, PRODUCT_OFFERINGS_GET_MEGA_POWERS_LIST);

        yield put(wizardLoaderStartAction());
        const megaPowers = yield call(requestSaga, url, EHttpMethod.GET);

        const existedMegaPowers = yield select(makeSelectProductOfferingsMegaPowers());
        if (existedMegaPowers.length === 0) {
            yield put(productOfferingsSetParamsAction({ megaPowers, initialMegaPowers: megaPowers }));
        }
    } catch (e: any) {
        const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
        const goBack = () => breadCrumbGoBack(breadCrumbsData);

        const translationProductOfferings = yield select(makeSelectTranslationWizardProductOfferings());

        yield call(checkResponseErrorWithModal, e, {
            message: translationProductOfferings.MegaPowersConfigurerStep.megaPowersListError,
            onCloseModal: goBack,
        });
    } finally {
        yield put(wizardLoaderStopAction());
    }
}

/*
 * -------------------------------------------
 *       LOAD DATA FOR NEXT STEP
 * -------------------------------------------
 */

export function* productOfferingsStepSelectMegaPowersNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    yield call(productOfferingsStepSelectMegaPowersLoadParams, action.payload.nextStep?.step);
}

export function* productOfferingsStepSelectMegaPowersUpdateMegaPowers(): SagaIterator {
    const url =
        API_HOSTNAME +
        stringFormat(WIZARD, EWizardName.PRODUCT_OFFERINGS) +
        stringFormat(WIZARD_ACTION, PRODUCT_OFFERINGS_UPDATE_MEGA_POWERS);

    const initialMegaPowers = yield select(makeSelectProductOfferingsInitialMegaPowers());
    const changedMegaPowers = yield select(makeSelectProductOfferingsMegaPowers());

    const activatedAndDeactivatedMegaPowers = mapMegaPowersToActivatedAndDeactivated(
        initialMegaPowers,
        changedMegaPowers,
    );

    try {
        if (activatedAndDeactivatedMegaPowers.toActivate || activatedAndDeactivatedMegaPowers.toDeactivate) {
            yield put(wizardLoaderStartAction());
            yield call(requestSaga, url, EHttpMethod.POST, activatedAndDeactivatedMegaPowers);
        }
    } catch (e: any) {
        const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
        const goBack = () => breadCrumbGoBack(breadCrumbsData);

        const translationProductOfferings = yield select(makeSelectTranslationWizardProductOfferings());

        yield call(checkResponseErrorWithModal, e, {
            message: translationProductOfferings.MegaPowersConfigurerStep.megaPowersListError,
            onCloseModal: goBack,
        });
    } finally {
        yield put(wizardLoaderStopAction());
    }
}

export function* productOfferingsStepSelectMegaPowersLoadParams(stepMeta: any): SagaIterator {
    yield put(
        productOfferingsSetParamsAction({
            productOfferings: stepMeta.productOfferings || [],
            subsCount: stepMeta.subsCount || 0,
        }),
    );
}

export function* productOfferingsStepSelectDateUpdateStorageSaga(): SagaIterator {
    const megaPowers = yield select(makeSelectProductOfferingsMegaPowers());

    yield put(wizardStorageUpdateStepAction({ megaPowers }));
}
